// Push Notifications Service
// Handles web push notification subscription and management

import env from '../config/env';
import { selectedCompanyHeader } from '../hooks/userCompanies.shared';

// VAPID public key - Generated using: npx web-push generate-vapid-keys
const VAPID_PUBLIC_KEY =
  'BMr5EGJpv3lnBn6XCVxStWATznHVuUC7gTGTzkmmD-OmuhziC4j17QJKGT56KlG2_bMBnzPvRO9_jmKwCgHt6yI';

interface PushSubscriptionData {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  userAgent?: string;
}

class PushNotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  constructor() {
    this.init();
  }

  private async init() {
    if (!('serviceWorker' in navigator)) {
      console.warn('[Push] Service workers not supported');
      return;
    }

    if (!('PushManager' in window)) {
      console.warn('[Push] Push notifications not supported');
      return;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('[Push] Service worker registered:', this.swRegistration);

      // Check if already subscribed
      this.subscription =
        await this.swRegistration.pushManager.getSubscription();

      if (this.subscription) {
        console.log('[Push] Already subscribed to push notifications');
        await this.sendSubscriptionToServer(this.subscription);
      }
    } catch (error) {
      console.error('[Push] Service worker registration failed:', error);
    }
  }

  // Check if push notifications are supported
  isSupported(): boolean {
    // Check for iOS Safari requirements
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = window.navigator.standalone === true;

    if (isIOS && !isStandalone) {
      return false; // iOS requires PWA installation
    }

    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // Check iOS requirements specifically
  getIOSRequirements(): {
    isIOS: boolean;
    isStandalone: boolean;
    canInstall: boolean;
  } {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = window.navigator.standalone === true;
    const canInstall =
      isIOS && !isStandalone && navigator.userAgent.includes('Safari');

    return {
      isIOS,
      isStandalone,
      canInstall,
    };
  }

  // Check backend subscription status
  private async checkBackendSubscriptionStatus(): Promise<boolean> {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn('[Push] No auth token available for backend check');
        return false;
      }

      const response = await fetch(
        `${env.apiUrl}/driver-mobile/push-subscription`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
        }
      );

      if (!response.ok) {
        console.warn(
          '[Push] Backend subscription check failed:',
          response.status
        );
        return false;
      }

      const result = await response.json();
      const hasActiveSubscriptions = result.success && result.count > 0;

      console.log('[Push] Backend subscription check:', {
        success: result.success,
        count: result.count,
        hasActive: hasActiveSubscriptions,
      });

      return hasActiveSubscriptions;
    } catch (error) {
      console.error(
        '[Push] Failed to check backend subscription status:',
        error
      );
      return false;
    }
  }

  // Get current subscription status (checks both browser AND backend)
  async getSubscriptionStatus(): Promise<{
    isSubscribed: boolean;
    permission: NotificationPermission;
    subscription: PushSubscription | null;
    backendSubscribed: boolean;
  }> {
    if (!this.isSupported()) {
      return {
        isSubscribed: false,
        permission: 'denied',
        subscription: null,
        backendSubscribed: false,
      };
    }

    const permission = Notification.permission;

    if (!this.swRegistration) {
      return {
        isSubscribed: false,
        permission,
        subscription: null,
        backendSubscribed: false,
      };
    }

    // Check browser subscription
    const subscription =
      await this.swRegistration.pushManager.getSubscription();
    const browserSubscribed = !!subscription;

    // Check backend subscription
    const backendSubscribed = await this.checkBackendSubscriptionStatus();

    // Only consider fully subscribed if BOTH browser and backend have the subscription
    const isFullySubscribed = browserSubscribed && backendSubscribed;

    console.log('[Push] Subscription status check:', {
      browserSubscribed,
      backendSubscribed,
      isFullySubscribed,
      permission,
    });

    return {
      isSubscribed: isFullySubscribed,
      permission,
      subscription,
      backendSubscribed,
    };
  }

  // Subscribe to push notifications
  async subscribe(): Promise<boolean> {
    try {
      if (!this.swRegistration) {
        throw new Error('Service worker not registered');
      }

      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.warn('[Push] Notification permission denied');
        return false;
      }

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

      // Subscribe to push notifications
      this.subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      console.log('[Push] Successfully subscribed to browser push manager:', {
        endpoint: this.subscription.endpoint.substring(0, 50) + '...',
        hasKeys: !!(
          this.subscription.getKey('p256dh') && this.subscription.getKey('auth')
        ),
      });

      // Send subscription to server - this is critical!
      try {
        await this.sendSubscriptionToServer(this.subscription);
        console.log('[Push] Full subscription process completed successfully');
        return true;
      } catch (serverError) {
        console.error(
          '[Push] Browser subscription succeeded but server registration failed:',
          serverError
        );

        // Unsubscribe from browser since server failed
        try {
          await this.subscription.unsubscribe();
          this.subscription = null;
        } catch (unsubError) {
          console.error(
            '[Push] Failed to clean up browser subscription:',
            unsubError
          );
        }

        return false;
      }
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      return false;
    }
  }

  /**
   * Sends this browser's existing subscription again, under the newly
   * selected company. The backend registers a device to the Driver row in the
   * company on the request, so after a switch the pushes kept coming from the
   * old company and none from the new one. Asks for no permission: a browser
   * that never subscribed has nothing to move.
   */
  async registerForSelectedCompany(): Promise<void> {
    const subscription =
      await this.swRegistration?.pushManager.getSubscription();
    if (subscription) await this.sendSubscriptionToServer(subscription);
  }

  // Unsubscribe from push notifications
  async unsubscribe(): Promise<boolean> {
    try {
      if (!this.subscription) {
        console.log('[Push] No active subscription to unsubscribe');
        return true;
      }

      // Unsubscribe from push notifications
      await this.subscription.unsubscribe();

      // Remove subscription from server
      await this.removeSubscriptionFromServer(this.subscription);

      this.subscription = null;
      console.log('[Push] Successfully unsubscribed');

      return true;
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      return false;
    }
  }

  // Send subscription to backend server
  private async sendSubscriptionToServer(
    subscription: PushSubscription
  ): Promise<void> {
    try {
      // Format data to match backend DTO structure
      const subscriptionData = {
        endpoint: subscription.endpoint,
        p256dhKey: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
        authKey: this.arrayBufferToBase64(subscription.getKey('auth')!),
        userAgent: navigator.userAgent,
      };

      // Get auth token
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn('[Push] No auth token available');
        throw new Error('No authentication token available');
      }

      console.log('[Push] Sending subscription to server:', {
        endpoint: subscriptionData.endpoint.substring(0, 50) + '...',
        hasP256dh: !!subscriptionData.p256dhKey,
        hasAuth: !!subscriptionData.authKey,
        userAgent: subscriptionData.userAgent,
      });

      // Send to backend
      const response = await fetch(
        `${env.apiUrl}/driver-mobile/push-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
          body: JSON.stringify(subscriptionData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Push] Server error:', response.status, errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log('[Push] Subscription sent to server successfully:', result);
    } catch (error) {
      console.error('[Push] Failed to send subscription to server:', error);
      throw error; // Re-throw so the calling code knows it failed
    }
  }

  // Remove subscription from backend server
  private async removeSubscriptionFromServer(
    subscription: PushSubscription
  ): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.warn('[Push] No auth token available');
        return;
      }

      const response = await fetch(
        `${env.apiUrl}/driver-mobile/push-subscription`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            ...selectedCompanyHeader(),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('[Push] Subscription removed from server successfully');
    } catch (error) {
      console.error('[Push] Failed to remove subscription from server:', error);
    }
  }

  // Utility function to convert VAPID key
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Utility function to convert ArrayBuffer to base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Test notification (for development)
  async sendTestNotification(): Promise<void> {
    if (this.subscription) {
      // This would typically be called from your backend
      console.log('[Push] Test notification would be sent via backend');
    } else {
      console.warn('[Push] No subscription available for test notification');
    }
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
